import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import UpdateIcon from '@mui/icons-material/Update';
import AssignmentService from '../../services/AssignmentService';
import { useTrackUsage } from '../../hooks/useTrackUsage';

const UpdateStatusDialog = ({ open, onClose, assignment, onUpdate }) => {
  useTrackUsage('UpdateStatusDialog', 'dialog', 'folder-browser', open);
  const [selectedStatus, setSelectedStatus] = useState(assignment?.status || 'ASSIGNED');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Reset status when assignment changes
  React.useEffect(() => {
    if (assignment) {
      setSelectedStatus(assignment.status || 'ASSIGNED');
    }
    // Clear messages when dialog opens
    setError(null);
    setSuccess(null);
  }, [assignment, open]);

  const statuses = [
    { value: 'ASSIGNED', label: 'Assigned' },
    { value: 'IN_PROGRESS', label: 'In Progress' },
    { value: 'COMPLETED', label: 'Completed' },
    { value: 'OVERDUE', label: 'Overdue' }
  ];

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const authUser = JSON.parse(localStorage.getItem('authUser'));
      const authToken = JSON.parse(localStorage.getItem('authToken'));
      
      // Call API to update status
      await AssignmentService.updateAssignmentStatus(
        assignment.id,
        { status: selectedStatus },
        authUser.email,
        authToken
      );
      
      setSuccess(`Status updated to ${selectedStatus}`);
      
      // Notify parent component about the update
      if (onUpdate) {
        onUpdate();
      }
      
      // Close dialog after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error("Error updating status:", error);
      setError("Failed to update status: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  if (!assignment) return null;

  return (
    <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="xs" 
        fullWidth
        onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <UpdateIcon sx={{ mr: 1 }} />
        Update Assignment Status
      </DialogTitle>
      
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        <Typography variant="subtitle2" gutterBottom>
          {assignment.languageName} - {assignment.videoName || 'Video'}
        </Typography>
        
        <Typography variant="caption" display="block" gutterBottom color="text.secondary">
          Assigned to: {assignment.assignedTo}
        </Typography>
        
        <Box sx={{ mt: 2 }}>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="status-label">Status</InputLabel>
            <Select
              labelId="status-label"
              value={selectedStatus}
              onChange={(e) => {
                e.stopPropagation();
                setSelectedStatus(e.target.value)
              }}
              onClick={(e) => e.stopPropagation()}
              label="Status"
              disabled={loading}
            >
              {statuses.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={loading || selectedStatus === assignment.status}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateStatusDialog;